import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../../Common/Loading';

// --- Interfaces ---
interface SupportRecord {
    login: string;
    surname: string;
    suburb: string;
    state: string;
    phone: string;
    date_of_sale: string;
    taken_by: string;
    action_date: string;
    type_reason: string;
    school_code: string;
}

const TutorSupportList: React.FC = () => {
    const API_BASE = 'https://staging.sysadmin.elearnoncloud.com/api/';
    const [searchParams] = useSearchParams();
    
    // Extracted from URL: ?Tutor=bharat
    const tutorLogin = searchParams.get('Tutor') || '';

    // --- State ---
    const [data, setData] = useState<SupportRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [dateFilter, setDateFilter] = useState<'current' | 'future'>('current');

    // --- Background Reset ---
    useEffect(() => {
        document.body.style.backgroundColor = '#ffffff';
    }, []);

    // --- Fetch Data ---
    useEffect(() => {
        const fetchList = async () => {
            if (!tutorLogin) return;
            setLoading(true);
            try {
                const res = await fetch(`${API_BASE}tutor_support_list`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        tutor_login: tutorLogin,
                        date_filter: dateFilter
                    })
                });
                const json = await res.json();
                
                if (json.error) {
                    alert(`Database Error: ${json.error}`);
                } else {
                    setData(json.results || []);
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchList();
    }, [tutorLogin, dateFilter]);

    // --- Handlers ---
    const handleBack = () => {
        window.location.href = "/SYSADMIN/Reports/TutorSupportReport";
    };

    const handleRowClick = (record: SupportRecord) => {
        const familyCode = "A";
        const sfl = "&S=A&F=AA&L=Admin"; // Replace with actual session logic if needed
        
        const url = `/Support/EL_AddEditTask?FamilyCode=${familyCode}&SchoolCode=${record.school_code}&Login=${record.login}&FilterType=CustEntry${sfl}`;
        window.location.href = url;
    };

    // --- Exact UI Styles Matching the Screenshot ---
    const styles = {
        // body text-align center forces the inline-block wrapper to horizontally center at the TOP of the page
        body: { fontFamily: 'Arial, sans-serif', padding: '20px', minHeight: '100vh' as const },
        
        // inline-block ensures the wrapper perfectly hugs the table width
        wrapper: { display: 'inline-block', textAlign: 'left' as const, width: '100%', maxWidth: '1000px' }, 
        
        // Header aligns the Title to center and Back button to the right edge
        headerContainer: { position: 'relative' as const, marginBottom: '5px', textAlign: 'center' as const, width: '100%' },
        title: { fontSize: '20pt', fontWeight: 'bold', color: 'black', margin: 0 },
        
        backBtn: { 
            position: 'absolute' as const, 
            right: 0, 
            top: '5px', 
            backgroundColor: '#328da8', 
            color: 'white', 
            fontWeight: 'bold', 
            fontSize: '9pt', 
            padding: '5px 12px', 
            border: '1px solid #000', 
            cursor: 'pointer', 
            borderRadius: '2px' 
        },

        // Radios
        radioContainer: { textAlign: 'center' as const, marginBottom: '10px', fontSize: '14px', color: '#000' },
        radioLabel: { cursor: 'pointer', margin: '0 5px', display: 'inline-flex', alignItems: 'center', color: '#000' },
        radioInput: { marginRight: '3px', cursor: 'pointer' },
        
        // Table formatting (Dotted borders exactly as shown in screenshot)
        tableWrapper: { width: '100%', overflowX: 'auto' as const, backgroundColor: 'white' },
        table: { width: '100%', borderCollapse: 'collapse' as const, border: '1px dotted #072129' },
        th: { backgroundColor: '#328da8', color: '#ffffff', padding: '8px 7px', textAlign: 'left' as const, border: '1px dotted #072129', fontSize: '13px', fontWeight: 'bold' },
        td: { padding: '10px 7px', border: '1px dotted #072129', color: 'black', fontSize: '13px', textAlign: 'left' as const }
    };

    return (
        <div style={styles.body}>
            {loading && <Loading />}

            <div style={styles.wrapper}>
                
                {/* 1. Header Area */}
                <div style={styles.headerContainer}>
                    <div style={styles.title}>Tutor Support List</div>
                    <button style={styles.backBtn} onClick={handleBack}>Back</button>
                </div>

                {/* 2. Radio Buttons */}
                <div style={styles.radioContainer}>
                    <label style={styles.radioLabel}>
                        <input 
                            type="radio" 
                            name="dateFilter" 
                            value="current" 
                            checked={dateFilter === 'current'} 
                            onChange={() => setDateFilter('current')} 
                            style={styles.radioInput}
                        /> 
                        Current
                    </label>
                    <label style={styles.radioLabel}>
                        <input 
                            type="radio" 
                            name="dateFilter" 
                            value="future" 
                            checked={dateFilter === 'future'} 
                            onChange={() => setDateFilter('future')} 
                            style={styles.radioInput}
                        /> 
                        Future
                    </label>
                </div>

                {/* 3. Empty State */}
                {!loading && data.length === 0 && (
                    <div style={{ fontSize: '20pt', color: 'black', textAlign: 'center', marginTop: '20px' }}>
                        No Records Found
                    </div>
                )}

                {/* 4. Grid Area */}
                {data.length > 0 && (
                    <div style={styles.tableWrapper}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, width: '40px' }}>Sl No</th>
                                    <th style={styles.th}>Login</th>
                                    <th style={styles.th}>Surname</th>
                                    <th style={styles.th}>Suburb</th>
                                    <th style={styles.th}>State</th>
                                    <th style={styles.th}>Phone</th>
                                    <th style={styles.th}>Date Of Sale</th>
                                    <th style={styles.th}>Taken By</th>
                                    <th style={styles.th}>Action Date</th>
                                    <th style={styles.th}>Type/Reason</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((row, index) => {
                                    // Row coloration matching: odd = light blue, even = white
                                    const bgColor = index % 2 === 0 ? '#e9f3f7' : '#ffffff';
                                    
                                    return (
                                        <tr 
                                            key={`${row.school_code}-${row.login}`} 
                                            style={{ backgroundColor: bgColor, cursor: 'pointer' }}
                                            onClick={() => handleRowClick(row)}
                                        >
                                            <td style={styles.td}>{index + 1}</td>
                                            <td style={styles.td}>{row.login}</td>
                                            <td style={styles.td}>{row.surname}</td>
                                            <td style={styles.td}>{row.suburb}</td>
                                            <td style={styles.td}>{row.state}</td>
                                            <td style={styles.td}>{row.phone}</td>
                                            <td style={styles.td}>{row.date_of_sale}</td>
                                            <td style={styles.td}>{row.taken_by}</td>
                                            <td style={styles.td}>{row.action_date}</td>
                                            <td style={styles.td}>{row.type_reason}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TutorSupportList;